import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/feature/DashboardLayout';
import { useAuth } from '../../lib/api/auth';
import { marketApi, vipApi } from '../../lib/api/endpoints';
import type { IncentiveTemplateSimple, IncentiveTemplateDetail, VIPInfo } from '../../lib/api/types';

const habitLabels: Record<number, string> = {
  1: '时间管理',
  2: '学习专注',
  3: '生活自理',
  4: '运动健康',
  5: '责任担当',
  6: '言行礼仪'
};

const habitColors: Record<number, string> = {
  1: 'bg-blue-100 text-blue-700',
  2: 'bg-sky-100 text-sky-700',
  3: 'bg-emerald-100 text-emerald-700',
  4: 'bg-amber-100 text-amber-700',
  5: 'bg-rose-100 text-rose-700',
  6: 'bg-teal-100 text-teal-700'
};

const tagColors: Record<string, string> = {
  '热门': 'bg-rose-500 text-white',
  '推荐': 'bg-blue-500 text-white',
  '精选': 'bg-sky-500 text-white',
  '免费': 'bg-emerald-500 text-white',
  'VIP': 'bg-gradient-to-r from-amber-400 to-orange-500 text-white',
  '爆款': 'bg-rose-500 text-white',
  '专家出品': 'bg-blue-800 text-white',
  '专家': 'bg-blue-800 text-white',
  '王牌': 'bg-gradient-to-r from-amber-400 to-orange-500 text-white',
  '新手推荐': 'bg-teal-500 text-white',
  '新手友好': 'bg-teal-500 text-white',
  '新品': 'bg-violet-500 text-white',
  '限时': 'bg-orange-500 text-white',
  '季节限定': 'bg-emerald-500 text-white',
  '3-6岁': 'bg-pink-500 text-white',
  '7-12岁': 'bg-purple-500 text-white',
  '13岁+': 'bg-indigo-500 text-white',
};

export default function MarketPage() {
  const { currentFamily } = useAuth();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<IncentiveTemplateSimple[]>([]);
  const [vipInfo, setVipInfo] = useState<VIPInfo | null>(null);
  const [filter, setFilter] = useState<'all' | 'free' | 'vip'>('all');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<IncentiveTemplateDetail | null>(null);
  const [showVipModal, setShowVipModal] = useState(false);
  const [showDetailLoading, setShowDetailLoading] = useState(false);

  // 检查是否是VIP和管理员
  const isVip = vipInfo?.vip_status === 2 || vipInfo?.status === 1;
  const canManage = currentFamily?.my_role === 1;

  // 加载数据
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [templatesRes, vipRes] = await Promise.all([
          marketApi.getTemplates(),
          currentFamily?.id ? vipApi.getVipInfo(currentFamily.id) : Promise.resolve(null)
        ]);

        if (templatesRes.code === 0 && templatesRes.data) {
          setTemplates(templatesRes.data);
        }
        if (vipRes?.code === 0 && vipRes.data) {
          setVipInfo(vipRes.data);
        }
      } catch (error) {
        console.error('Failed to load market data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentFamily?.id]);

  // 获取VIP状态（兼容is_vip和is_vip_required两个字段）
  const getIsVip = (template: IncentiveTemplateSimple | IncentiveTemplateDetail): boolean => {
    return template.is_vip_required || template.is_vip || false;
  };

  // 过滤模板
  const filtered = templates.filter(t => {
    if (filter === 'free') return !getIsVip(t);
    if (filter === 'vip') return getIsVip(t);
    return true;
  });

  // 加载模板详情
  const handleSelectTemplate = async (templateId: string) => {
    if (selectedTemplateId === templateId) {
      setSelectedTemplateId(null);
      setSelectedTemplate(null);
      return;
    }

    setShowDetailLoading(true);
    try {
      const res = await marketApi.getTemplate(templateId);
      if (res.code === 0 && res.data) {
        setSelectedTemplate(res.data);
        setSelectedTemplateId(templateId);
      }
    } catch (error) {
      console.error('Failed to load template detail:', error);
    } finally {
      setShowDetailLoading(false);
    }
  };

  // 复制模板
  const handleAdopt = async (template: IncentiveTemplateSimple | IncentiveTemplateDetail) => {
    if (!canManage) {
      alert('记录员无权使用激励市场功能，仅管理员可操作');
      return;
    }

    if (getIsVip(template) && !isVip) {
      setShowVipModal(true);
      return;
    }

    if (!currentFamily?.id) return;

    try {
      const res = await marketApi.copyTemplate({
        template_id: template.id,
        family_id: currentFamily.id
      });

      if (res.code === 0 && res.data) {
        setSelectedTemplateId(null);
        setSelectedTemplate(null);
        // 显示复制结果（包含跳过的重复规则）
        alert(res.data.message || '方案已成功复制到您的家庭！');
      }
    } catch (error) {
      console.error('Failed to copy template:', error);
    }
  };

  // 获取标签
  const getTags = (template: IncentiveTemplateSimple | IncentiveTemplateDetail) => {
    const tagLabels: Record<string, string> = {
      // 英文key→中文
      'hot': '热门', 'recommended': '推荐', 'selected': '精选', 'free': '免费',
      'expert': '专家出品', 'beginner': '新手推荐', 'popular': '爆款', 'flagship': '王牌',
      'new': '新品', 'limited': '限时', 'seasonal': '季节限定',
      'age_3_6': '3-6岁', 'age_7_12': '7-12岁', 'age_13_plus': '13岁+',
      // 中文→中文（运营端直接存中文标签时的映射）
      '热门': '热门', '推荐': '推荐', '精选': '精选', '免费': '免费',
      '专家': '专家出品', '新手友好': '新手推荐', '爆款': '爆款', '王牌': '王牌',
      '新品': '新品', '限时': '限时', '季节限定': '季节限定',
      '3-6岁': '3-6岁', '7-12岁': '7-12岁', '13岁+': '13岁+',
    };

    const tags: string[] = [];
    if (getIsVip(template)) tags.push('VIP');
    else tags.push('免费');

    // 解析数据库中的tags字段
    if (template.tags) {
      try {
        const tagList = typeof template.tags === 'string' ? JSON.parse(template.tags) : template.tags;
        if (Array.isArray(tagList)) {
          tagList.forEach((t: string) => {
            if (tagLabels[t] && !tags.includes(tagLabels[t])) {
              tags.push(tagLabels[t]);
            }
          });
        }
      } catch (e) {}
    }

    return tags;
  };

  // 获取习惯标签
  const getHabitTags = (habitTypes?: string) => {
    if (!habitTypes) return [];
    try {
      const types = typeof habitTypes === 'string' ? JSON.parse(habitTypes) : habitTypes;
      return Array.isArray(types) ? types : [];
    } catch (e) {
      return [];
    }
  };

  // 解析习惯类型为数字数组
  const parseHabitTypes = (habitTypes?: string): number[] => {
    if (!habitTypes) return [];
    try {
      const types = typeof habitTypes === 'string' ? JSON.parse(habitTypes) : habitTypes;
      return Array.isArray(types) ? types.map(Number) : [];
    } catch (e) {
      return [];
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-4 md:p-6 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-loader-4-line animate-spin text-4xl text-blue-500"></i>
            <p className="text-gray-400 mt-2">加载中...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-black text-blue-900">激励市场</h1>
            <p className="text-gray-400 text-sm mt-0.5">发现优质奖惩方案，一键复用到家庭</p>
          </div>
        </div>

        {/* Stats Banner */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-700 rounded-2xl p-5 mb-5 relative overflow-hidden">
          <div className="absolute -right-8 -top-8 w-40 h-40 bg-white/5 rounded-full"></div>
          <div className="flex items-center gap-6 relative z-10">
            <div>
              <p className="text-white/60 text-xs mb-0.5">市场方案总数</p>
              <p className="text-3xl font-black text-white">{templates.length}</p>
            </div>
            <div className="w-px h-10 bg-white/20"></div>
            <div>
              <p className="text-white/60 text-xs mb-0.5">免费方案</p>
              <p className="text-3xl font-black text-emerald-300">{templates.filter(t => !getIsVip(t)).length}</p>
            </div>
            <div className="w-px h-10 bg-white/20"></div>
            <div>
              <p className="text-white/60 text-xs mb-0.5">VIP专属</p>
              <p className="text-3xl font-black text-amber-300">{templates.filter(t => getIsVip(t)).length}</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-white/60 text-xs mb-1">累计被使用</p>
              <p className="text-xl font-black text-white">{templates.reduce((a, t) => a + ((t as any).usage_count || t.use_count || 0), 0).toLocaleString()}次</p>
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-1 bg-blue-50 p-1 rounded-xl w-fit mb-5">
          {([['all', '全部方案'], ['free', '免费方案'], ['vip', 'VIP专属']] as const).map(([key, label]) => (
            <button key={key} onClick={() => setFilter(key as any)} className={`px-5 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${filter === key ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(template => {
            const tags = getTags(template);
            const habitTypes = parseHabitTypes(template.habit_types);
            return (
              <div key={template.id} className={`bg-white rounded-2xl overflow-hidden border-2 transition-all hover:-translate-y-0.5 cursor-pointer ${getIsVip(template) && !isVip ? 'opacity-90' : ''} ${selectedTemplateId === template.id ? 'border-blue-500' : 'border-transparent'}`} onClick={() => handleSelectTemplate(template.id)}>
                {/* Card Header */}
                <div className={`p-4 ${getIsVip(template) ? 'bg-gradient-to-r from-amber-50 to-orange-50' : 'bg-gradient-to-r from-blue-50 to-sky-50'}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex gap-1 flex-wrap">
                      {tags.map(tag => (
                        <span key={tag} className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${tagColors[tag] || 'bg-gray-100 text-gray-600'}`}>{tag}</span>
                      ))}
                    </div>
                    {getIsVip(template) && !isVip && (
                      <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-400 ml-2 flex-shrink-0">
                        <i className="ri-lock-fill text-white text-xs"></i>
                      </div>
                    )}
                  </div>
                  <h3 className="font-black text-blue-900 text-sm">{template.title || template.name}</h3>
                </div>

                {/* Card Body */}
                <div className="p-4">
                  <p className="text-xs text-gray-500 leading-relaxed mb-3 line-clamp-2">{template.description || '暂无描述'}</p>

                  {/* 习惯标签 */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {habitTypes.map(h => (
                      <span key={h} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${habitColors[h]}`}>{habitLabels[h]}</span>
                    ))}
                  </div>

                  {/* 规则数、奖品数、使用次、改善率 */}
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    <div className="bg-blue-50 rounded-lg p-2 text-center">
                      <div className="text-sm font-black text-blue-900">{template.rule_count || 0}</div>
                      <div className="text-[9px] text-gray-400">规则数</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-2 text-center">
                      <div className="text-sm font-black text-blue-900">{template.item_count || 0}</div>
                      <div className="text-[9px] text-gray-400">奖品数</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-2 text-center">
                      <div className="text-sm font-black text-blue-900">{(template as any).usage_count || template.use_count || 0}</div>
                      <div className="text-[9px] text-gray-400">使用次</div>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-2 text-center">
                      {(() => {
                        const rate = Number(template.improvement_rate || 0);
                        return (
                          <>
                            <div className={`text-sm font-black ${rate > 0 ? 'text-emerald-600' : rate < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                              {rate > 0 ? '+' : ''}{rate.toFixed(1)}%
                            </div>
                            <div className="text-[9px] text-gray-400">{template.cycle_days || 27}日见效</div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <button
                    onClick={e => { e.stopPropagation(); handleAdopt(template); }}
                    className={`w-full mt-3 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                      !canManage
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : !getIsVip(template)
                          ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:opacity-90'
                          : isVip
                            ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white hover:opacity-90'
                            : 'bg-gray-100 text-gray-400 border border-gray-200'
                    }`}
                  >
                    {!canManage ? '🔒 记录员无权限' : !getIsVip(template) ? '免费复制方案' : isVip ? 'VIP复制方案' : '🔒 VIP专属 · 升级解锁'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Template Detail Modal */}
      {selectedTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => { setSelectedTemplateId(null); setSelectedTemplate(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-[600px] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {showDetailLoading ? (
              <div className="p-12 text-center">
                <i className="ri-loader-4-line animate-spin text-4xl text-blue-500"></i>
                <p className="text-gray-400 mt-2">加载中...</p>
              </div>
            ) : (
              <>
                <div className={`p-5 ${getIsVip(selectedTemplate) ? 'bg-gradient-to-r from-amber-50 to-orange-50' : 'bg-gradient-to-r from-blue-50 to-sky-50'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex gap-1 flex-wrap mb-2">
                      {getTags(selectedTemplate).map(tag => <span key={tag} className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${tagColors[tag] || 'bg-gray-100 text-gray-600'}`}>{tag}</span>)}
                    </div>
                    <button onClick={() => { setSelectedTemplateId(null); setSelectedTemplate(null); }} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-white/50">
                      <i className="ri-close-line text-xl"></i>
                    </button>
                  </div>
                  <h2 className="font-black text-blue-900 text-xl mb-1">{selectedTemplate.title || selectedTemplate.name}</h2>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">{(selectedTemplate as any).usage_count || selectedTemplate.use_count || 0}次使用</span>
                  </div>
                </div>
                <div className="p-5 space-y-4">
                  <p className="text-sm text-gray-600 leading-relaxed">{selectedTemplate.description || '暂无描述'}</p>

                  {/* 涵盖习惯维度 */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2">涵盖习惯维度</p>
                    <div className="flex flex-wrap gap-1.5">
                      {parseHabitTypes(selectedTemplate.habit_types).map(h => (
                        <span key={h} className={`text-xs px-3 py-1 rounded-full font-medium ${habitColors[h]}`}>{habitLabels[h]}</span>
                      ))}
                    </div>
                  </div>

                  {/* 规则列表 - 4列瀑布流 */}
                  {selectedTemplate.rules && selectedTemplate.rules.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-2">包含规则</p>

                      {/* 奖励规则 - 4列网格 */}
                      {selectedTemplate.rules.filter((r: any) => r.rule_type === 1).length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-emerald-600 font-medium mb-1.5">🎁 奖励规则</p>
                          <div className="grid grid-cols-4 gap-1.5">
                            {selectedTemplate.rules.filter((r: any) => r.rule_type === 1).map((r: any, idx: number) => {
                              const ruleData = typeof r.rule_data === 'string' ? JSON.parse(r.rule_data) : r.rule_data;
                              return (
                                <div key={idx} className="bg-emerald-50 rounded-lg px-2 py-1.5">
                                  <div className="text-xs text-emerald-700 font-medium truncate" title={ruleData.name}>{ruleData.name}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* 惩罚规则 - 4列网格 */}
                      {selectedTemplate.rules.filter((r: any) => r.rule_type === 2).length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-rose-600 font-medium mb-1.5">⚡ 惩罚规则</p>
                          <div className="grid grid-cols-4 gap-1.5">
                            {selectedTemplate.rules.filter((r: any) => r.rule_type === 2).map((r: any, idx: number) => {
                              const ruleData = typeof r.rule_data === 'string' ? JSON.parse(r.rule_data) : r.rule_data;
                              return (
                                <div key={idx} className="bg-rose-50 rounded-lg px-2 py-1.5">
                                  <div className="text-xs text-rose-700 font-medium truncate" title={ruleData.name}>{ruleData.name}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* 兑换商品 - 4列网格 */}
                      {selectedTemplate.rules.filter((r: any) => r.rule_type === 3).length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-amber-600 font-medium mb-1.5">🎁 兑换商品</p>
                          <div className="grid grid-cols-4 gap-1.5">
                            {selectedTemplate.rules.filter((r: any) => r.rule_type === 3).map((r: any, idx: number) => {
                              const ruleData = typeof r.rule_data === 'string' ? JSON.parse(r.rule_data) : r.rule_data;
                              return (
                                <div key={idx} className="bg-amber-50 rounded-lg px-2 py-1.5">
                                  <div className="text-xs text-amber-700 font-medium truncate" title={ruleData.name}>{ruleData.name}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 改善率信息 */}
                  <div className="bg-emerald-50 rounded-xl p-3">
                    <div className="flex items-center gap-2">
                      <i className="ri-line-chart-fill text-emerald-500 text-lg"></i>
                      <div>
                        <p className="text-sm font-semibold text-emerald-700">平均 30 天见效</p>
                        <p className="text-xs text-emerald-600">改善率 {selectedTemplate.completion_rate || 82}%</p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleAdopt(selectedTemplate)}
                    className={`w-full py-3 rounded-xl font-bold text-white transition-all cursor-pointer whitespace-nowrap ${
                      !getIsVip(selectedTemplate)
                        ? 'bg-gradient-to-r from-blue-500 to-blue-700 hover:opacity-90'
                        : isVip
                          ? 'bg-gradient-to-r from-amber-400 to-orange-500 hover:opacity-90'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {!getIsVip(selectedTemplate) ? '一键复制到我的家庭' : isVip ? 'VIP一键复制方案' : '升级VIP解锁此方案'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* VIP Modal */}
      {showVipModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowVipModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-[360px] text-center" onClick={e => e.stopPropagation()}>
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 mx-auto mb-4">
              <i className="ri-vip-crown-fill text-white text-3xl"></i>
            </div>
            <h3 className="font-black text-[#0d1f5c] text-lg mb-2">VIP专属方案</h3>
            <p className="text-gray-500 text-sm mb-4">该方案为VIP专属内容，升级会员可无限获取市场优质方案</p>
            <button className="w-full bg-gradient-to-r from-amber-400 to-orange-500 text-white py-3 rounded-xl font-bold cursor-pointer whitespace-nowrap mb-2">
              立即升级 VIP · 98元/年
            </button>
            <button onClick={() => setShowVipModal(false)} className="text-gray-400 text-sm cursor-pointer">暂不升级</button>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
